import { useState, useEffect, useCallback, useMemo } from "react"
import { useOutletContext, useSearchParams } from "react-router-dom"

interface DashboardContext {
  propertyId?: string
}
import { toast } from "sonner"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Copy01Icon,
  Tick01Icon,
  Delete01Icon,
  Add01Icon,
  Key01Icon,
  Alert01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  CheckmarkCircle02Icon,
  Loading03Icon,
  Github01Icon,
  PlayIcon,
  Link01Icon,
  McpServerIcon,
  EyeIcon,
  ViewOffIcon,
  SparklesIcon,
} from "@hugeicons/core-free-icons"
import { cn } from "@/lib/utils"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { FeatureGate } from "@/components/shared/FeatureGate"
import { PageShell, PageHeader, SegmentedControl } from "@/components/shared/layout"
import { SettingsTabs } from "@/components/shared/settings/SettingsKit"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { useSiteSafe } from "@/lib/contexts/SiteContext"
import { useAuth } from "@/lib/contexts/AuthContext"
import { useSubscription } from "@/lib/contexts/SubscriptionContext"
import { usePlanUsage } from "@/hooks/usePlanUsage"
import { isServerFeatureDenied } from "@/lib/utils/subscriptionEntitlement"
import {
  getApiKeys, createApiKey, revokeApiKey,
  getAccountApiKeys, createAccountApiKey, revokeAccountApiKey,
  ApiAccessForbiddenError,
} from "@/lib/api/api-keys"
import type { ApiKey } from "@/lib/api/api-keys"
import { getTrafficSummary } from "@/lib/api/external-api"
import { track } from "@/lib/utils/track"
import {
  logApiAccessBlocked,
  logApiAccessEvent,
  reportApiAccessFailure,
} from "@/lib/observability/apiAccess"
import {
  generateMcpSetupPrompt,
  generateMcpOAuthSetupPrompt,
  generateSkillsSetup,
  generateFirstTestCommand,
} from "@/lib/utils/generateAgentInstructions"
import type { AgentFormat } from "@/lib/utils/generateAgentInstructions"
import { McpHero } from "@/components/shared/mcp/McpIntro"
import {
  SKILL_WORKFLOWS,
  SKILLS_API_CONNECTED_NOTE,
  SKILLS_CONTEXT_ONLY_NOTE,
  SKILLS_EXPLAINER,
  SKILLS_MCP_NOTE,
} from "@/data/skillsGuidance"
import { EXTERNAL_API_BASE_URL as BASE_URL, API_ORIGIN, MCP_URL } from "@/lib/constants/agentConnect"

const SKILLS_REPO_URL = "https://github.com/Humblytics/humblytics-marketing-skills"
const SKILLS_DIRECTORY_URL = "https://humblytics.com/skills"

// ─── Skills catalog ───────────────────────────────────────────────────────────
type Skill = { id: string; name: string; description: string; api: boolean }

const SKILLS: Skill[] = [
  { id: "cro-optimizer", name: "cro-optimizer", description: "Live funnel analysis + scored A/B test hypotheses", api: true },
  { id: "ab-test-generator", name: "ab-test-generator", description: "Generate & launch no-code split tests from analytics", api: true },
  { id: "heatmap-analyst", name: "heatmap-analyst", description: "Click, scroll & rage-click friction analysis", api: true },
  { id: "revenue-attributor", name: "revenue-attributor", description: "Ad spend to Stripe revenue, ROAS + reallocation", api: true },
  { id: "funnel-reporter", name: "funnel-reporter", description: "End-to-end SaaS funnel reporting", api: true },
  { id: "page-cro", name: "page-cro", description: "10-point landing page conversion audit", api: false },
  { id: "email-sequences", name: "email-sequences", description: "Onboarding, nurture & re-engagement drips", api: false },
  { id: "seo-strategist", name: "seo-strategist", description: "Keyword research, gaps, on-page + technical SEO", api: false },
  { id: "marketing-strategist", name: "marketing-strategist", description: "Funnels, GTM, positioning, growth strategy", api: false },
  { id: "copywriting", name: "copywriting", description: "Conversion copy for pages, emails & ads", api: false },
  { id: "ad-expert", name: "ad-expert", description: "Paid ads: Meta, Google, TikTok, LinkedIn", api: false },
  { id: "content-strategist", name: "content-strategist", description: "Articles, newsletters, social, video, SEO", api: false },
]

// ─── REST endpoint groups ─────────────────────────────────────────────────────
type Endpoint = { method: string; path: string; description: string }
type EndpointGroup = { id: string; label: string; tagline: string; endpoints: Endpoint[] }

const ENDPOINT_GROUPS: EndpointGroup[] = [
  {
    id: "read", label: "Read", tagline: "analytics, recommendations & heatmaps",
    endpoints: [
      { method: "GET", path: "properties/{propertyId}/traffic/summary", description: "Aggregate metrics: pageviews, sessions, bounce rate, avg session duration" },
      { method: "GET", path: "properties/{propertyId}/traffic/trends", description: "Timeseries pageviews & unique visitors" },
      { method: "GET", path: "properties/{propertyId}/traffic/breakdown", description: "Device, location, referrer, UTM breakdowns" },
      { method: "GET", path: "properties/{propertyId}/pages/breakdown", description: "All page performance: views, visitors, scroll depth, bounce rate" },
      { method: "GET", path: "properties/{propertyId}/clicks/breakdown", description: "Click data across all pages with top targets" },
      { method: "GET", path: "properties/{propertyId}/forms/breakdown", description: "Form submissions across all pages" },
      { method: "GET", path: "properties/{propertyId}/split-test-recommendations", description: "AI split test suggestions for a page. Requires ?page=/path" },
      { method: "GET", path: "properties/{propertyId}/funnels/suggestions", description: "AI-generated funnel suggestions for a page. Requires ?page=/path" },
    ],
  },
  {
    id: "decide", label: "Decide", tagline: "funnels & split-test results",
    endpoints: [
      { method: "GET", path: "properties/{propertyId}/funnels", description: "Run a funnel query. Params: steps (JSON), start, end, timezone, mode, breakdownBy" },
      { method: "GET", path: "properties/{propertyId}/saved-funnels", description: "List all saved funnel configurations" },
      { method: "GET", path: "properties/{propertyId}/split-tests", description: "List experiments. Optional ?status=active|complete" },
      { method: "GET", path: "properties/{propertyId}/split-tests/{experimentId}", description: "Experiment details with per-variant metrics" },
    ],
  },
  {
    id: "ship", label: "Ship", tagline: "split-tests & variants",
    endpoints: [
      { method: "POST", path: "properties/{propertyId}/split-tests", description: 'Create a split test. Body: { name, page, type: "nocode", variants, goal?, auto_stop_days? }' },
      { method: "PATCH", path: "properties/{propertyId}/split-tests/{experimentId}", description: "Update an active experiment. Body: { name?, auto_stop_days? }" },
      { method: "POST", path: "properties/{propertyId}/split-tests/{experimentId}/stop", description: "Stop a running experiment. Body: { reason? }" },
      { method: "POST", path: "properties/{propertyId}/saved-funnels", description: "Create or update a saved funnel. Body: { name, steps }" },
      { method: "DELETE", path: "properties/{propertyId}/saved-funnels/{funnelId}", description: "Delete a saved funnel" },
    ],
  },
  {
    id: "onboard", label: "Onboard", tagline: "properties & connectors",
    endpoints: [
      { method: "GET", path: "/api/v1/properties/{propertyId}/ads-attribution", description: "Per-campaign funnel: impressions to clicks to sessions to revenue. Requires ?startDate&endDate" },
      { method: "GET", path: "/api/google-ads-connections", description: "List Google Ads connections. Query: ?propertyId={propertyId}" },
      { method: "GET", path: "/api/meta-connections", description: "List Meta Ads connections. Query: ?propertyId={propertyId}" },
      { method: "GET", path: "/api/meta-connections/{id}/campaigns", description: "All campaigns across ad accounts in a connection" },
    ],
  },
]

// ─── Clipboard helper ─────────────────────────────────────────────────────────
async function copyToClipboardRobust(text: string): Promise<boolean> {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try { await navigator.clipboard.writeText(text); return true } catch { /* fall through */ }
  }
  try {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.cssText = "position:fixed;left:-9999px;top:-9999px;opacity:0"
    document.body.appendChild(textarea)
    textarea.focus(); textarea.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(textarea)
    return ok
  } catch { return false }
}

// ─── Dialog MCP prompt tabs ───────────────────────────────────────────────────
const MCP_PROMPT_TABS: { id: AgentFormat; label: string }[] = [
  { id: "claude-code", label: "Claude Code" },
  { id: "codex", label: "Codex CLI" },
  { id: "generic", label: "Generic" },
]

function ApiAccessInner() {
  const ctx = useOutletContext<DashboardContext>()
  const workspaceCtx = useWorkspaceSafe()
  const siteCtx = useSiteSafe()
  const { token } = useAuth()
  const { isReadOnly, openPricingModal } = useSubscription()

  const workspace = workspaceCtx?.workspace ?? null
  const selectedPropertyId = workspaceCtx?.selectedPropertyId ?? null
  const enabledProperties = workspaceCtx?.enabledProperties ?? []

  const currentPropertyId = selectedPropertyId || ctx?.propertyId || workspaceCtx?.propertyId || siteCtx?.currentSite?.propertyId || enabledProperties?.[0]?.propertyId || ""
  const currentSiteLabel =
    enabledProperties.find((p) => p.propertyId === currentPropertyId)?.domain
    || siteCtx?.currentSite?.domain
    || "this site"

  // ── Account-level keys ──
  const [accountKeys, setAccountKeys] = useState<ApiKey[]>([])
  const [isLoadingAccountKeys, setIsLoadingAccountKeys] = useState(true)
  const [accountKeysError, setAccountKeysError] = useState<string | null>(null)

  // ── Property-level keys ──
  const [propertyKeys, setPropertyKeys] = useState<ApiKey[]>([])
  const [isLoadingPropertyKeys, setIsLoadingPropertyKeys] = useState(true)
  const [propertyKeysError, setPropertyKeysError] = useState<string | null>(null)
  const [propRevokedOpen, setPropRevokedOpen] = useState(false)

  // ── MCP OAuth setup dialog ──
  const [showMcpDialog, setShowMcpDialog] = useState(false)
  const [mcpOAuthFormat, setMcpOAuthFormat] = useState<AgentFormat>("claude-code")

  // ── Create dialog ──
  // scope: "account" (default) or "property"
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [createScope, setCreateScope] = useState<"account" | "property">("account")
  const [newKeyName, setNewKeyName] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null)
  const [newlyCreatedScope, setNewlyCreatedScope] = useState<"account" | "property">("account")
  const [mcpPromptFormat, setMcpPromptFormat] = useState<AgentFormat>("claude-code")

  // ── Revoke dialog ──
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null)

  // ── Page tabs (Keys | Skills | REST) + keys sub-scope ──
  type PageTab = "keys" | "skills" | "rest"
  type KeysScope = "account" | "property"
  const [pageTab, setPageTab] = useState<PageTab>("keys")
  const [keysScope, setKeysScope] = useState<KeysScope>("account")
  const [chainsOpen, setChainsOpen] = useState(true)
  const [curlEnvMode, setCurlEnvMode] = useState(true)
  const [endpointsOpen, setEndpointsOpen] = useState(false)

  // ── Copy state ──
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // ── Verify state ──
  const [isVerifying, setIsVerifying] = useState(false)
  const [verifyResult, setVerifyResult] = useState<unknown | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  // ── Key reveal state ──
  const [revealedKeyIds, setRevealedKeyIds] = useState<Set<string>>(new Set())

  const activeAccountKeys = useMemo(() => accountKeys.filter(k => k.isActive), [accountKeys])
  const activePropertyKeys = useMemo(() => propertyKeys.filter(k => k.isActive), [propertyKeys])
  const revokedPropertyKeys = useMemo(() => propertyKeys.filter(k => !k.isActive), [propertyKeys])

  const fetchAccountKeys = useCallback(async () => {
    if (!token) return
    setIsLoadingAccountKeys(true)
    setAccountKeysError(null)
    try {
      const keys = await getAccountApiKeys(token)
      setAccountKeys(keys)
    } catch (err) {
      if (err instanceof ApiAccessForbiddenError) {
        logApiAccessBlocked("plan_gate_hit", {}, { keyScope: "account" })
      } else {
        reportApiAccessFailure("load_keys", err, {}, "account")
      }
      setAccountKeysError(err instanceof ApiAccessForbiddenError ? "upgrade-required" : "error")
      setAccountKeys([])
    } finally {
      setIsLoadingAccountKeys(false)
    }
  }, [token])

  const fetchPropertyKeys = useCallback(async () => {
    if (!currentPropertyId || !token) { setIsLoadingPropertyKeys(false); return }
    setIsLoadingPropertyKeys(true)
    setPropertyKeysError(null)
    try {
      const keys = await getApiKeys(currentPropertyId, token)
      setPropertyKeys(keys)
    } catch (err) {
      if (err instanceof ApiAccessForbiddenError) {
        logApiAccessBlocked("plan_gate_hit", { propertyId: currentPropertyId }, {
          keyScope: "property",
        })
      } else {
        reportApiAccessFailure("load_keys", err, { propertyId: currentPropertyId }, "property")
      }
      setPropertyKeysError(err instanceof ApiAccessForbiddenError ? "upgrade-required" : "error")
      setPropertyKeys([])
    } finally {
      setIsLoadingPropertyKeys(false)
    }
  }, [currentPropertyId, token])

  useEffect(() => { fetchAccountKeys() }, [fetchAccountKeys])
  useEffect(() => { fetchPropertyKeys() }, [fetchPropertyKeys])

  // ── Copy helper ──
  const copyToClipboard = useCallback(async (text: string, field: string) => {
    const ok = await copyToClipboardRobust(text)
    if (ok) {
      setCopiedField(field)
      toast.success("Copied to clipboard")
      setTimeout(() => setCopiedField(null), 2000)
      return true
    }
    toast.error("Failed to copy")
    return false
  }, [])

  // ── MCP OAuth setup prompt (no key — agent does the OAuth flow) ──
  const mcpOAuthSetupPrompt = useMemo(
    () => generateMcpOAuthSetupPrompt(mcpOAuthFormat, {
      mcpUrl: MCP_URL,
      apiBaseUrl: API_ORIGIN,
    }),
    [mcpOAuthFormat]
  )

  // ── MCP setup prompt (shown after key creation, with literal key) ──
  const mcpSetupPrompt = useMemo(
    () =>
      newlyCreatedKey
        ? generateMcpSetupPrompt(mcpPromptFormat, {
            mcpUrl: MCP_URL,
            apiKey: newlyCreatedKey,
            // Account keys don't scope to a property — omit the header
            propertyId: newlyCreatedScope === "account" ? "" : currentPropertyId,
          })
        : "",
    [mcpPromptFormat, newlyCreatedKey, newlyCreatedScope, currentPropertyId]
  )

  // ── Skills setup ──
  const skillsSetup = useMemo(
    () => generateSkillsSetup({ propertyId: currentPropertyId, format: "claude-code" }),
    [currentPropertyId]
  )

  // ── Curl reference ──
  const firstTestCommand = useMemo(
    () => generateFirstTestCommand({ propertyId: currentPropertyId, baseUrl: BASE_URL, useEnvVar: curlEnvMode }),
    [currentPropertyId, curlEnvMode]
  )

  // Existing keys stay listed after a cancel so the customer can see and revoke
  // what's out there, but minting a new key is a paid action — a live key hits
  // the API long after the subscription ended. `isReadOnly` covers the caller's
  // own cancel; the server flag covers what the client can't see — a workspace
  // owner's cancel, which membership (inferred from cached plan state) hides.
  const { metadata: planUsageMetadata } = usePlanUsage({ propertyId: currentPropertyId })
  const createKeyBlocked =
    isReadOnly || isServerFeatureDenied(planUsageMetadata, "apiAccess")

  const openCreateDialog = (scope: "account" | "property" = "account") => {
    if (createKeyBlocked) {
      openPricingModal()
      return
    }
    setCreateScope(scope)
    setNewlyCreatedKey(null)
    setMcpPromptFormat("claude-code")
    setNewKeyName("")
    setShowCreateDialog(true)
  }

  // URL sync: ?tab=keys|skills|rest · ?keys=account|property · ?setup=mcp opens create dialog
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const tab = searchParams.get("tab")
    if (tab === "keys" || tab === "skills" || tab === "rest") {
      setPageTab(tab)
    }
    const keys = searchParams.get("keys")
    if (keys === "account" || keys === "property") {
      setKeysScope(keys)
    }
    if (searchParams.get("setup") === "mcp") {
      setShowMcpDialog(true)
      const next = new URLSearchParams(searchParams)
      next.delete("setup")
      setSearchParams(next, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setTab = (tab: PageTab) => {
    setPageTab(tab)
    const next = new URLSearchParams(searchParams)
    if (tab === "keys") next.delete("tab")
    else next.set("tab", tab)
    setSearchParams(next, { replace: true })
  }

  const setKeysScopeTab = (scope: KeysScope) => {
    setKeysScope(scope)
    const next = new URLSearchParams(searchParams)
    if (scope === "account") next.delete("keys")
    else next.set("keys", scope)
    setSearchParams(next, { replace: true })
  }

  // ── Create key ──
  const handleCreateKey = async () => {
    if (!token || !newKeyName.trim() || createKeyBlocked) return
    if (createScope === "property" && !currentPropertyId) return
    setIsCreating(true)
    try {
      const result = createScope === "account"
        ? await createAccountApiKey(newKeyName.trim(), token)
        : await createApiKey(currentPropertyId, newKeyName.trim(), token)
      setNewlyCreatedKey(result.fullKey)
      setNewlyCreatedScope(createScope)
      if (createScope === "account") {
        setAccountKeys(prev => [result.apiKey, ...prev])
      } else {
        setPropertyKeys(prev => [result.apiKey, ...prev])
      }
      setNewKeyName("")
      track("api_key_generated", token, { scope: createScope })
      logApiAccessEvent("key_created", { propertyId: currentPropertyId }, createScope)
      toast.success("API key created")
    } catch (error) {
      if (error instanceof ApiAccessForbiddenError) {
        logApiAccessBlocked("plan_gate_hit", { propertyId: currentPropertyId }, {
          keyScope: createScope,
        })
      } else {
        reportApiAccessFailure("create_key", error, { propertyId: currentPropertyId }, createScope)
      }
      toast.error("Failed to create API key")
    } finally {
      setIsCreating(false)
    }
  }

  // ── Revoke key ──
  const handleRevokeKey = async () => {
    if (!token || !keyToRevoke) return
    try {
      if (keyToRevoke.scope === "account") {
        await revokeAccountApiKey(keyToRevoke.id, token)
        setAccountKeys(prev => prev.filter(k => k.id !== keyToRevoke.id))
      } else {
        await revokeApiKey(currentPropertyId, keyToRevoke.id, token)
        setPropertyKeys(prev => prev.filter(k => k.id !== keyToRevoke.id))
      }
      logApiAccessEvent("key_revoked", { propertyId: currentPropertyId }, keyToRevoke.scope)
      toast.success("API key revoked")
    } catch (error) {
      reportApiAccessFailure(
        "revoke_key",
        error,
        { propertyId: currentPropertyId },
        keyToRevoke.scope,
      )
      toast.error("Failed to revoke API key")
    } finally {
      setKeyToRevoke(null)
    }
  }

  // ── Verify connection ──
  const handleVerify = async () => {
    if (!currentPropertyId || !token) return
    setIsVerifying(true)
    setVerifyError(null)
    setVerifyResult(null)
    try {
      const res = await getTrafficSummary("PAST_7_D", currentPropertyId, token)
      setVerifyResult(res)
      track("connection_verified", token)
      logApiAccessEvent("connection_verified", { propertyId: currentPropertyId }, "property")
      toast.success("Connection verified")
    } catch (error) {
      logApiAccessBlocked("verification_failed", { propertyId: currentPropertyId }, {
        reason: error instanceof Error ? error.message : "unknown",
      })
      setVerifyError("Request failed. Check the property has data and your plan includes API access.")
      toast.error("Verification failed")
    } finally {
      setIsVerifying(false)
    }
  }

  const verifySummary = useMemo(() => {
    if (!verifyResult || typeof verifyResult !== "object") return null
    const data = (verifyResult as { data?: Record<string, number> }).data
    if (!data) return null
    return { pageViews: data.page_views ?? 0, uniqueVisitors: data.unique_visitors ?? 0, sessions: data.total_sessions ?? 0 }
  }, [verifyResult])

  const copyMcpSetupPrompt = async () => {
    const ok = await copyToClipboard(mcpSetupPrompt, "mcp-prompt")
    if (ok) track("mcp_prompt_copied", token, { agent: mcpPromptFormat })
  }

  const toggleReveal = (id: string) => setRevealedKeyIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const isLoading = isLoadingAccountKeys || isLoadingPropertyKeys
  if (isLoading) return <ApiAccessSkeleton />

  const upgradeRequired = accountKeysError === "upgrade-required" || propertyKeysError === "upgrade-required"
  const totalActiveKeys = activeAccountKeys.length + activePropertyKeys.length
  const apiSkills = SKILLS.filter((s) => s.api)
  const contextSkills = SKILLS.filter((s) => !s.api)

  return (
    <PageShell className="flex flex-col gap-6">
      <PageHeader
        title="API & MCP"
        description="Ask about your data from Claude or Codex, or call the API from your own code."
        className="mb-0"
      />

      {/* ── MCP hero (always on, never tabbed away) ── */}
      <McpHero onConnect={() => setShowMcpDialog(true)} />

      {/* ── Upgrade wall ── */}
      {upgradeRequired && (
        <Card>
          <CardContent className="flex flex-col items-center py-10 text-center space-y-3">
            <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
              <HugeiconsIcon icon={Key01Icon} size={20} className="text-warning" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">API access required</p>
              <p className="text-xs text-muted-foreground max-w-xs">Available on Business plan or higher.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => window.open("https://app.humblytics.com/billing", "_self")}>
              Upgrade plan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Page tabs: Keys | Skills | REST ── */}
      <SettingsTabs
        label="API & MCP sections"
        active={pageTab}
        onChange={setTab}
        tabs={[
          { id: "keys", label: totalActiveKeys > 0 ? `API keys · ${totalActiveKeys}` : "API keys" },
          { id: "skills", label: "Skills" },
          { id: "rest", label: "REST reference" },
        ]}
      />

      {/* ── Keys tab ── */}
      {pageTab === "keys" && !upgradeRequired && (
        <div className="well -mt-2"><Card className="!py-0 !gap-0">
          <CardHeader className="border-b border-border/40 px-4 pb-3 pt-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-medium text-foreground">API keys</CardTitle>
                <CardDescription className="mt-0.5 text-[11px]">
                  {keysScope === "account"
                    ? "Account keys work across every site. Use one for MCP."
                    : `Scoped to ${currentSiteLabel}`}
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 shrink-0"
                onClick={() => openCreateDialog(keysScope)}
                disabled={keysScope === "property" && !currentPropertyId}
              >
                <HugeiconsIcon icon={createKeyBlocked ? ArrowRight01Icon : Add01Icon} size={13} className="mr-1.5" />
                {createKeyBlocked ? "Upgrade" : "Create"}
              </Button>
            </div>
            <SegmentedControl
              className="mt-3"
              value={keysScope}
              onChange={setKeysScopeTab}
              options={[
                { value: "account", label: `Account (${activeAccountKeys.length})` },
                { value: "property", label: `Property (${activePropertyKeys.length})` },
              ]}
            />
          </CardHeader>
          <CardContent className="pt-0">
            {keysScope === "account" ? (
              accountKeysError === "error" ? (
                <div className="flex items-center gap-2 py-6 px-4 text-sm text-muted-foreground">
                  <HugeiconsIcon icon={Alert01Icon} size={14} className="text-destructive shrink-0" />
                  Failed to load account keys. Try refreshing.
                </div>
              ) : activeAccountKeys.length === 0 ? (
                <div className="py-8 flex flex-col items-center gap-3 text-center">
                  <div className="w-9 h-9 rounded-full bg-muted/50 border border-border/40 flex items-center justify-center">
                    <HugeiconsIcon icon={Key01Icon} size={16} className="text-muted-foreground" />
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-foreground">No account keys yet</p>
                    <p className="text-xs text-muted-foreground">Create one to connect your agent to all properties.</p>
                  </div>
                  <Button size="sm" onClick={() => openCreateDialog("account")}>
                    <HugeiconsIcon icon={createKeyBlocked ? ArrowRight01Icon : Add01Icon} size={13} className="mr-1.5" />
                    {createKeyBlocked ? "Upgrade to create keys" : "Create account key"}
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-border/20">
                  {activeAccountKeys.map(key => (
                    <KeyRow
                      key={key.id}
                      apiKey={key}
                      revealed={revealedKeyIds.has(key.id)}
                      onToggleReveal={() => toggleReveal(key.id)}
                      onCopy={() => copyToClipboard(key.keyPrefix, `key-${key.id}`)}
                      copied={copiedField === `key-${key.id}`}
                      onRevoke={() => setKeyToRevoke(key)}
                    />
                  ))}
                </div>
              )
            ) : propertyKeysError === "error" ? (
              <div className="flex items-center gap-2 py-6 px-4 text-sm text-muted-foreground">
                <HugeiconsIcon icon={Alert01Icon} size={14} className="text-destructive shrink-0" />
                Failed to load property keys. Try refreshing.
              </div>
            ) : !currentPropertyId ? (
              <p className="py-6 px-4 text-sm text-muted-foreground text-center">Select a property to manage its keys.</p>
            ) : activePropertyKeys.length === 0 ? (
              <div className="py-8 flex flex-col items-center gap-3 text-center">
                <p className="text-sm text-muted-foreground">No property keys for this site.</p>
                <Button size="sm" variant="outline" onClick={() => openCreateDialog("property")}>
                  <HugeiconsIcon icon={createKeyBlocked ? ArrowRight01Icon : Add01Icon} size={13} className="mr-1.5" />
                  {createKeyBlocked ? "Upgrade to create keys" : "Create property key"}
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border/20">
                {activePropertyKeys.map(key => (
                  <KeyRow
                    key={key.id}
                    apiKey={key}
                    revealed={revealedKeyIds.has(key.id)}
                    onToggleReveal={() => toggleReveal(key.id)}
                    onCopy={() => copyToClipboard(key.keyPrefix, `key-${key.id}`)}
                    copied={copiedField === `key-${key.id}`}
                    onRevoke={() => setKeyToRevoke(key)}
                  />
                ))}
              </div>
            )}

            {keysScope === "property" && revokedPropertyKeys.length > 0 && (
              <Collapsible open={propRevokedOpen} onOpenChange={setPropRevokedOpen}>
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors px-4 py-3 w-full border-t border-border/20">
                    <HugeiconsIcon icon={ArrowDown01Icon} size={13} className={cn("transition-transform duration-200", propRevokedOpen && "rotate-180")} />
                    Revoked keys
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0">{revokedPropertyKeys.length}</Badge>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="divide-y divide-border/20">
                    {revokedPropertyKeys.map(key => (
                      <div key={key.id} className="flex items-center justify-between px-4 py-3 opacity-50">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground truncate">{key.name}</p>
                          <p className="text-[11px] font-mono text-muted-foreground/70">{key.keyPrefix}••••••••••••</p>
                        </div>
                        <Badge variant="outline" className="text-[11px] shrink-0">Revoked</Badge>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </CardContent>
        </Card></div>
      )}

      {/* ── Skills tab ── */}
      {pageTab === "skills" && (
        <div className="well -mt-2"><Card className="!py-0 !gap-0">
          <CardHeader className="border-b border-border/40 px-4 pb-3 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-medium text-foreground">Marketing skills</CardTitle>
                <CardDescription className="mt-0.5 text-[11px]">
                  Easier playbooks for plain-English jobs. {apiSkills.length} pull live data
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={SKILLS_REPO_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <HugeiconsIcon icon={Github01Icon} size={13} />GitHub
                </a>
                <a href={SKILLS_DIRECTORY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <HugeiconsIcon icon={Link01Icon} size={13} />/skills
                </a>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pt-4 pb-4 space-y-4">
            <div className="rounded-md border border-border/30 bg-muted/10 px-3 py-2.5 space-y-2">
              <p className="text-[11px] text-muted-foreground leading-snug">{SKILLS_EXPLAINER}</p>
              <div className="space-y-1 text-[11px] text-muted-foreground leading-snug">
                <p>
                  <Badge variant="secondary" className="text-[11px] mr-1.5 align-middle">API</Badge>
                  {SKILLS_API_CONNECTED_NOTE}
                </p>
                <p>{SKILLS_CONTEXT_ONLY_NOTE}</p>
              </div>
              <p className="text-[11px] text-muted-foreground/80 leading-snug border-t border-border/20 pt-2">
                {SKILLS_MCP_NOTE}
              </p>
            </div>

            <div className="rounded-lg border border-border/30 overflow-hidden">
              <div className="grid grid-cols-[1fr_auto] gap-2 px-3 py-2 border-b border-border/30 bg-muted/30">
                <span className="text-xs font-medium text-muted-foreground">Skill</span>
                <span className="text-xs font-medium text-muted-foreground">API</span>
              </div>
              {apiSkills.map((skill, i) => (
                <div key={skill.id} className={cn("grid grid-cols-[1fr_auto] items-center gap-2 px-3 py-2", i < apiSkills.length - 1 && "border-b border-border/20")}>
                  <div className="min-w-0">
                    <p className="font-mono text-[12px] text-foreground">{skill.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{skill.description}</p>
                  </div>
                  <Badge variant="secondary" className="text-[11px]">API</Badge>
                </div>
              ))}
            </div>

            {contextSkills.length > 0 && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <HugeiconsIcon
                      icon={ArrowDown01Icon}
                      size={12}
                      className="transition-transform duration-200 group-data-[state=open]:rotate-180"
                    />
                    {contextSkills.length} context-only skills
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 rounded-lg border border-border/30 overflow-hidden">
                    {contextSkills.map((skill, i) => (
                      <div
                        key={skill.id}
                        className={cn(
                          "px-3 py-2",
                          i < contextSkills.length - 1 && "border-b border-border/20",
                        )}
                      >
                        <p className="font-mono text-[12px] text-foreground">{skill.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{skill.description}</p>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            <Button size="sm" className="h-8" onClick={() => copyToClipboard(skillsSetup, "skills-setup")}>
              <HugeiconsIcon icon={copiedField === "skills-setup" ? Tick01Icon : Copy01Icon} size={14} className="mr-1.5" />
              {copiedField === "skills-setup" ? "Copied" : `Add ${SKILLS.length} skills to your agent`}
            </Button>
            <Collapsible open={chainsOpen} onOpenChange={setChainsOpen}>
              <CollapsibleTrigger asChild>
                <button type="button" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <HugeiconsIcon icon={ArrowDown01Icon} size={14} className={cn("transition-transform duration-200", chainsOpen && "rotate-180")} />
                  {chainsOpen ? "Hide example jobs" : "Show example jobs you can ask for"}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 pt-3">
                  {SKILL_WORKFLOWS.map((wf) => (
                    <div key={wf.id} className="rounded-md border border-border/20 bg-muted/10 px-3 py-2 space-y-1.5">
                      <p className="text-[12px] text-foreground/90 leading-snug">&ldquo;{wf.prompt}&rdquo;</p>
                      <p className="text-[11px] text-muted-foreground leading-snug">{wf.outcome}</p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {wf.chain.map((s, i) => (
                          <div key={s} className="flex items-center gap-1.5">
                            <span className="inline-flex items-center rounded-md border border-border/30 bg-card px-2 py-0.5 text-[11px] font-medium text-foreground/80">{s}</span>
                            {i < wf.chain.length - 1 && <HugeiconsIcon icon={ArrowRight01Icon} size={12} className="text-muted-foreground/60" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card></div>
      )}

      {/* ── REST tab ── */}
      {pageTab === "rest" && (
        <div className="well -mt-2"><Card className="!py-0 !gap-0">
          <CardHeader className="border-b border-border/40 px-4 pb-3 pt-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-medium text-foreground">REST API Reference</CardTitle>
                <CardDescription className="text-[11px]">Base: {BASE_URL}</CardDescription>
              </div>
              <SegmentedControl
                value={curlEnvMode ? "env" : "literal"}
                onChange={(v) => setCurlEnvMode(v === "env")}
                options={[
                  { value: "env", label: "Env var" },
                  { value: "literal", label: "Literal" },
                ]}
              />
            </div>
          </CardHeader>
          <CardContent className="px-4 pt-4 pb-4 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-medium text-muted-foreground">Example request</p>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => copyToClipboard(firstTestCommand, "ref-curl")}>
                  <HugeiconsIcon icon={copiedField === "ref-curl" ? Tick01Icon : Copy01Icon} size={12} className="mr-1" />
                  {copiedField === "ref-curl" ? "Copied" : "Copy"}
                </Button>
              </div>
              <pre className="p-3 rounded-md text-xs whitespace-pre-wrap break-all bg-muted/30 border border-border/30 font-mono">
                <code className="text-foreground/90">{firstTestCommand}</code>
              </pre>
              {!curlEnvMode && (
                <p className="text-[11px] text-warning dark:text-warning">
                  Literal mode is for one-off testing only. Always reference <code className="font-mono">$HUMBLYTICS_API_KEY</code> in committed commands.
                </p>
              )}
            </div>
            <div className="space-y-2.5">
              <p className="text-[11px] font-medium text-muted-foreground">Test connection</p>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm" className="h-8" onClick={handleVerify} disabled={isVerifying || !currentPropertyId}>
                  {isVerifying ? <HugeiconsIcon icon={Loading03Icon} size={13} className="mr-1.5 animate-spin" /> : <HugeiconsIcon icon={PlayIcon} size={13} className="mr-1.5" />}
                  {isVerifying ? "Testing..." : "Run test call"}
                </Button>
                <span className="font-mono text-[11px] text-muted-foreground">GET traffic/summary</span>
              </div>
              {verifyError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                  <HugeiconsIcon icon={Alert01Icon} size={14} className="shrink-0 mt-0.5" />{verifyError}
                </div>
              )}
              {verifyResult != null && (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-3">
                    <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} className="text-success dark:text-success shrink-0" />
                    <p className="text-xs font-medium text-foreground">200 OK, connection verified</p>
                  </div>
                  {verifySummary && (
                    <div className="grid grid-cols-3 gap-2">
                      <VerifyMetric label="Page views" value={verifySummary.pageViews} />
                      <VerifyMetric label="Unique visitors" value={verifySummary.uniqueVisitors} />
                      <VerifyMetric label="Sessions" value={verifySummary.sessions} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <Collapsible open={endpointsOpen} onOpenChange={setEndpointsOpen}>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {ENDPOINT_GROUPS.map((g) => (
                  <div key={g.id} className="text-[11px]">
                    <span className="font-medium text-foreground">{g.label}</span>
                    <span className="text-muted-foreground"> {g.tagline}</span>
                  </div>
                ))}
              </div>
              <CollapsibleTrigger asChild>
                <button type="button" className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <HugeiconsIcon icon={ArrowDown01Icon} size={14} className={cn("transition-transform duration-200", endpointsOpen && "rotate-180")} />
                  {endpointsOpen ? "Hide endpoints" : "Show all endpoints"}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-4 space-y-4">
                  {ENDPOINT_GROUPS.map((group) => (
                    <div key={group.id} className="space-y-1.5">
                      <div className="flex items-baseline gap-2">
                        <h4 className="text-[12px] font-medium text-foreground">{group.label}</h4>
                        <span className="text-[11px] text-muted-foreground">{group.tagline}</span>
                      </div>
                      {group.endpoints.map((ep) => (
                        <div key={ep.path} className="flex items-start gap-2.5 px-3 py-2 rounded-md border border-border/20 bg-muted/10">
                          <Badge variant="outline" className="text-[11px] font-mono shrink-0 mt-0.5 text-muted-foreground border-border/40">
                            {ep.method}
                          </Badge>
                          <div className="min-w-0 space-y-0.5">
                            <p className="font-mono text-[12px] text-foreground break-all">{ep.path}</p>
                            <p className="text-[11px] text-muted-foreground">{ep.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card></div>
      )}

      {/* ── MCP OAuth Setup Dialog ── */}
      <Dialog open={showMcpDialog} onOpenChange={setShowMcpDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Setup Humblytics MCP</DialogTitle>
            <DialogDescription>
              Copy this prompt and paste it into your agent. It will walk you through connecting your account automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center rounded-md border border-border/30 p-0.5 w-fit">
              {MCP_PROMPT_TABS.map((tab) => (
                <button key={tab.id} type="button" onClick={() => setMcpOAuthFormat(tab.id)}
                  className={cn("px-2.5 py-1 rounded text-[11px] font-medium transition-colors",
                    mcpOAuthFormat === tab.id ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground")}>
                  {tab.label}
                </button>
              ))}
            </div>
            <pre className="p-3 rounded-md text-[11px] leading-relaxed whitespace-pre-wrap break-words bg-muted/30 border border-border/30 font-mono max-h-[260px] overflow-y-auto scrollbar-hide">
              <code className="text-foreground/90">{mcpOAuthSetupPrompt}</code>
            </pre>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(mcpOAuthSetupPrompt, "mcp-oauth-prompt")}>
              <HugeiconsIcon icon={copiedField === "mcp-oauth-prompt" ? Tick01Icon : Copy01Icon} size={13} className="mr-1.5" />
              {copiedField === "mcp-oauth-prompt" ? "Copied" : "Copy prompt"}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowMcpDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Key Dialog ── */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className={cn(newlyCreatedKey ? "sm:max-w-lg" : "sm:max-w-md")}>
          <DialogHeader>
            <DialogTitle>{newlyCreatedKey ? "Key created" : "Create API key"}</DialogTitle>
            <DialogDescription>
              {newlyCreatedKey
                ? "Copy your key now. It won't be shown again. Then wire up MCP below."
                : createScope === "account"
                  ? "Account keys work across all your properties. Recommended for MCP."
                  : "Property key scoped to the currently selected property."}
            </DialogDescription>
          </DialogHeader>

          {newlyCreatedKey ? (
            <div className="space-y-4">
              <div className="p-3 rounded-md border border-warning/30 bg-warning/5">
                <p className="text-xs text-warning dark:text-warning font-medium mb-2">
                  Copy now. It won't be shown again.
                </p>
                <div className="flex items-center gap-2 p-2.5 rounded-md bg-card border border-border cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => copyToClipboard(newlyCreatedKey, "newKey")}>
                  <span className="flex-1 text-xs font-mono text-foreground break-all">{newlyCreatedKey}</span>
                  <HugeiconsIcon icon={copiedField === "newKey" ? Tick01Icon : Copy01Icon} size={14} className={copiedField === "newKey" ? "text-success" : "text-muted-foreground"} />
                </div>
              </div>
              <Separator />
              <div className="space-y-2.5">
                <div>
                  <p className="text-sm font-medium text-foreground">Wire up MCP</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Paste this into your coding agent. Key already filled in.</p>
                </div>
                <SegmentedControl
                  value={mcpPromptFormat}
                  onChange={setMcpPromptFormat}
                  options={MCP_PROMPT_TABS.map((tab) => ({ value: tab.id, label: tab.label }))}
                />
                <pre className="p-3 rounded-md text-[11px] leading-relaxed whitespace-pre-wrap break-words bg-muted/30 border border-border/30 font-mono max-h-[220px] overflow-y-auto scrollbar-hide">
                  <code className="text-foreground/90">{mcpSetupPrompt}</code>
                </pre>
                <Button variant="outline" size="sm" onClick={copyMcpSetupPrompt}>
                  <HugeiconsIcon icon={copiedField === "mcp-prompt" ? Tick01Icon : Copy01Icon} size={13} className="mr-1.5" />
                  {copiedField === "mcp-prompt" ? "Copied" : "Copy MCP setup prompt"}
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowCreateDialog(false)}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <SegmentedControl
                value={createScope}
                onChange={setCreateScope}
                options={[
                  { value: "account", label: "Account-wide" },
                  { value: "property", label: "Property" },
                ]}
              />
              {createScope === "property" && currentPropertyId && (
                <p className="text-[11px] text-muted-foreground">
                  Scoped to: <code className="font-mono">{currentPropertyId.slice(0, 16)}…</code>
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="key-name">Key name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., Production agent, CI/CD"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && newKeyName.trim() && handleCreateKey()}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateKey} disabled={!newKeyName.trim() || isCreating || (createScope === "property" && !currentPropertyId)}>
                  {isCreating ? "Creating..." : "Create key"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Revoke Confirmation ── */}
      <AlertDialog open={!!keyToRevoke} onOpenChange={() => setKeyToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any assistant or script using "{keyToRevoke?.name}" loses access right away. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep key</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleRevokeKey}>
              Revoke key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  )
}

// ─── Key row ──────────────────────────────────────────────────────────────────
function KeyRow({
  apiKey,
  revealed,
  onToggleReveal,
  onCopy,
  copied,
  onRevoke,
}: {
  apiKey: ApiKey
  revealed: boolean
  onToggleReveal: () => void
  onCopy: () => void
  copied: boolean
  onRevoke: () => void
}) {
  const maskedKey = `${apiKey.keyPrefix}••••••••••••••••`
  // "Used today" / "Used Oct 12", the app's date style, not the locale's 10/15/2026.
  const dateLabel = apiKey.lastUsedAt
    ? `Used ${friendlyDay(apiKey.lastUsedAt)}`
    : `Created ${friendlyDay(apiKey.createdAt)}`

  return (
    <div className="flex min-h-12 items-center justify-between gap-3 px-[18px] py-2">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <p className="min-w-0 max-w-[220px] flex-1 truncate text-sm font-medium text-foreground">{apiKey.name}</p>
        <p className="hidden truncate font-mono text-xs text-foreground/60 md:block">
          {revealed ? apiKey.keyPrefix : maskedKey}
        </p>
        <p className="ml-auto hidden shrink-0 text-xs text-foreground/60 sm:block">{dateLabel}</p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onToggleReveal}
          title={revealed ? "Hide" : "Show prefix"}
          className="size-7 text-muted-foreground hover:text-foreground"
        >
          <HugeiconsIcon icon={revealed ? ViewOffIcon : EyeIcon} size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onCopy}
          title="Copy prefix"
          className="size-7 text-muted-foreground hover:text-foreground"
        >
          <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRevoke}
          title="Revoke key"
          // Quiet until hovered: one red button per row read as an alarm on a calm list.
          className="ml-1 h-[30px] w-auto rounded-lg px-2.5 text-xs font-medium text-muted-foreground hover:bg-danger-tint hover:text-danger"
        >
          Revoke
        </Button>
      </div>
    </div>
  )
}

function friendlyDay(value: string | number | Date): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '–'
  const today = new Date()
  const days = Math.round((new Date(today.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== today.getFullYear() && { year: 'numeric' }) })
}

// ─── Verify metric tile ───────────────────────────────────────────────────────
function VerifyMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border/30 bg-muted/20 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-base font-medium text-foreground tabular-nums">{value.toLocaleString()}</p>
    </div>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function ApiAccessSkeleton() {
  return (
    <PageShell maxWidth="max-w-4xl" className="space-y-5">
      <div className="space-y-1.5">
        <Skeleton className="h-5 w-44" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Card className="border-border/30 !py-0 !gap-0">
        <CardHeader className="border-b border-border/40 pb-3 pt-4">
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-8 w-28 rounded-md" />
        </CardContent>
      </Card>
      <Skeleton className="h-7 w-48 rounded-md" />
      <Card className="border-border/30 !py-0 !gap-0">
        <CardHeader className="border-b border-border/40 pb-3 pt-4">
          <div className="flex justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {[0, 1].map(i => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-border/20 last:border-0">
              <div className="flex items-center gap-3">
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3 w-40" />
              </div>
              <div className="flex gap-0.5">
                {[0, 1, 2].map(j => <Skeleton key={j} className="size-7 rounded-md" />)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </PageShell>
  )
}

export default function ApiAccess() {
  return (
    <FeatureGate feature="apiAccess">
      <ApiAccessInner />
    </FeatureGate>
  )
}
